import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Type, AlertCircle, Mic, Users, CheckCircle, BookOpen } from 'lucide-react';
import clsx from 'clsx';
import { UploadSectionProps, UploadMethod, TranscriptPreset } from '../types/analysis';
import { FILE_UPLOAD } from '../constants';
import { LiveRecording } from './LiveRecording';
import { PresetService } from '../services';

export const UploadSection: React.FC<UploadSectionProps> = ({
  transcript,
  onTranscriptChange,
  file,
  onFileChange,
  audioUrl,
  onAudioUrlChange,
  onAsyncJobStart,
  numSpeakers,
  onNumSpeakersChange,
  error,
  sessionId,
  salesStage,
  onRecordingReady,
  selectedPreset,
  onPresetSelect,
  onAudioFileUpload,
  uploadedAudioFile,
  onClearUploadedAudio,
}) => {
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>('text');
  const [dragActive, setDragActive] = useState(false);
  const [presets, setPresets] = useState<TranscriptPreset[]>([]);
  const [loadingPreset, setLoadingPreset] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // Load available presets on component mount
  useEffect(() => {
    const availablePresets = PresetService.getAvailablePresets();
    setPresets(availablePresets);
  }, []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const fileName = droppedFile.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

      // Check both MIME type and file extension for better compatibility
      const isValidMimeType = FILE_UPLOAD.ACCEPTED_MIME_TYPES.includes(droppedFile.type as any);
      const isValidExtension = FILE_UPLOAD.ACCEPTED_TYPES.includes(fileExtension as any);

      if (isValidMimeType || isValidExtension) {
        // Determine if it's an audio file by extension or MIME type
        const isAudioFile = fileExtension === '.mp3' || fileExtension === '.wav' ||
                           fileExtension === '.m4a' || fileExtension === '.ogg' ||
                           fileExtension === '.webm' || fileExtension === '.flac' ||
                           droppedFile.type.startsWith('audio/');

        if (isAudioFile) {
          setUploadMethod('audio');
          if (onAudioFileUpload) {
            onAudioFileUpload(droppedFile);
          }
        } else {
          onFileChange(droppedFile);
          setUploadMethod('file');
        }
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      // Handle audio files differently - upload them immediately
      if (selectedFile.type.startsWith('audio/')) {
        setUploadMethod('audio');
        if (onAudioFileUpload) {
          onAudioFileUpload(selectedFile);
        }
      } else {
        onFileChange(selectedFile);
        setUploadMethod('file');
      }
    }
  };

  const removeFile = () => {
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle preset selection
  const handlePresetSelect = async (presetId: string) => {
    if (selectedPreset === presetId) {
      // Deselect preset if clicking the same one
      onPresetSelect(null);
      onTranscriptChange('');
      return;
    }

    setLoadingPreset(true);
    try {
      const presetTranscript = await PresetService.loadPresetTranscript(presetId);
      onPresetSelect(presetId);
      onTranscriptChange(presetTranscript);

      // Clear other inputs when selecting preset
      onFileChange(null);
      onAudioUrlChange('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error loading preset:', error);
      // Handle error - could show in error state
    } finally {
      setLoadingPreset(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="glass-card rounded-3xl p-8 backdrop-blur-xl">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
            Prześlij transkrypt
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Wklej tekst transkryptu, prześlij plik PDF/TXT/JSON/SRT lub plik audio, aby rozpocząć
          </p>
        </div>

        <div className="grid grid-cols-5 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl mb-6 gap-1">
          <button
            onClick={() => setUploadMethod('preset')}
            className={clsx(
              'flex flex-col items-center justify-center space-y-1 py-3 px-2 rounded-xl transition-all duration-200',
              uploadMethod === 'preset'
                ? 'bg-white dark:bg-gray-700 shadow-lg text-primary-600 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
            )}
          >
            <BookOpen className="w-5 h-5" />
            <span className="font-medium text-xs">Presety</span>
          </button>
          <button
            onClick={() => setUploadMethod('text')}
            className={clsx(
              'flex flex-col items-center justify-center space-y-1 py-3 px-2 rounded-xl transition-all duration-200',
              uploadMethod === 'text'
                ? 'bg-white dark:bg-gray-700 shadow-lg text-primary-600 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
            )}
          >
            <Type className="w-5 h-5" />
            <span className="font-medium text-xs">Tekst</span>
          </button>
          <button
            onClick={() => setUploadMethod('file')}
            className={clsx(
              'flex flex-col items-center justify-center space-y-1 py-3 px-2 rounded-xl transition-all duration-200',
              uploadMethod === 'file'
                ? 'bg-white dark:bg-gray-700 shadow-lg text-primary-600 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
            )}
          >
            <Upload className="w-5 h-5" />
            <span className="font-medium text-xs">Plik</span>
          </button>
          <button
            onClick={() => setUploadMethod('audio')}
            className={clsx(
              'flex flex-col items-center justify-center space-y-1 py-3 px-2 rounded-xl transition-all duration-200',
              uploadMethod === 'audio'
                ? 'bg-white dark:bg-gray-700 shadow-lg text-primary-600 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
            )}
          >
            <Mic className="w-5 h-5" />
            <span className="font-medium text-xs">Audio</span>
          </button>
          <button
            onClick={() => setUploadMethod('record')}
            className={clsx(
              'flex flex-col items-center justify-center space-y-1 py-3 px-2 rounded-xl transition-all duration-200',
              uploadMethod === 'record'
                ? 'bg-white dark:bg-gray-700 shadow-lg text-primary-600 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
            )}
          >
            <div className="w-5 h-5 bg-red-500 rounded-full animate-pulse"></div>
            <span className="font-medium text-xs">Nagraj</span>
          </button>
        </div>

        {uploadMethod === 'preset' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset.id)}
                  disabled={loadingPreset}
                  className={clsx(
                    'relative p-6 text-left border-2 rounded-2xl transition-all duration-200 disabled:opacity-50',
                    selectedPreset === preset.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20 shadow-lg scale-105'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-950/10'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                        {preset.name}
                      </h3>
                      {preset.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {preset.description}
                        </p>
                      )}
                    </div>
                    {selectedPreset === preset.id && (
                      <CheckCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 ml-2" />
                    )}
                  </div>

                  {loadingPreset && selectedPreset === preset.id && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 rounded-2xl flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {selectedPreset && transcript && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20
                              border border-green-200 dark:border-green-800 rounded-2xl p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-green-800 dark:text-green-200">
                    Transkrypt załadowany pomyślnie
                  </span>
                </div>
                <div className="text-sm text-green-700 dark:text-green-300 bg-white/50 dark:bg-green-950/20 rounded-xl p-4 max-h-48 overflow-y-auto">
                  {transcript.split('\n').slice(0, 10).join('\n')}
                  {transcript.split('\n').length > 10 && <div className="mt-2 text-green-600">... i więcej</div>}
                </div>
              </div>
            )}
          </div>
        ) : uploadMethod === 'text' ? (
          <div className="space-y-4">
            <textarea
              value={transcript}
              onChange={(e) => onTranscriptChange(e.target.value)}
              placeholder="Wklej tutaj transkrypt spotkania..."
              rows={12}
              className="w-full p-4 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 
                         rounded-2xl resize-none text-gray-800 dark:text-gray-200 placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                         transition-all duration-200"
            />
          </div>
        ) : uploadMethod === 'file' ? (
          <div className="space-y-4">
            {!file ? (
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={clsx(
                  'border-2 border-dashed rounded-3xl p-12 text-center cursor-pointer transition-all duration-300',
                  dragActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20 scale-105'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-950/10'
                )}
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Upuść plik tutaj lub kliknij, aby przeglądać
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  Pliki PDF, TXT, JSON, SRT do 50MB
                </p>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 
                              border border-green-200 dark:border-green-800 rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">
                        {file.name}
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={removeFile}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 
                               font-medium transition-colors duration-200"
                  >
                    Usuń
                  </button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept={FILE_UPLOAD.ACCEPTED_TYPES.join(',')}
              className="hidden"
            />
          </div>
        ) : uploadMethod === 'audio' ? (
          // Audio upload section
          <div className="space-y-6">
            {/* Number of speakers selection */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Liczba mówców
                </label>
              </div>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <button
                    key={num}
                    onClick={() => onNumSpeakersChange(num)}
                    className={clsx(
                      'w-12 h-12 rounded-xl font-medium text-sm transition-all duration-200',
                      numSpeakers === num
                        ? 'bg-purple-600 text-white shadow-lg scale-105'
                        : 'bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/20'
                    )}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Wybierz liczbę mówców, aby poprawić jakość transkrypcji
              </p>
            </div>

            {/* File Upload */}
            {!uploadedAudioFile ? (
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={clsx(
                  'border-2 border-dashed rounded-3xl p-12 text-center cursor-pointer transition-all duration-300',
                  dragActive
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/20 scale-105'
                    : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-950/10'
                )}
              >
                <Mic className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Upuść plik audio tutaj lub kliknij, aby przeglądać
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  Pliki MP3, WAV, M4A lub OGG do 50MB
                </p>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20
                              border border-green-200 dark:border-green-800 rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">
                        Plik audio przesłany pomyślnie
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        {uploadedAudioFile} • Gotowy do analizy
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Clear uploaded audio file state in parent
                      if (onClearUploadedAudio) onClearUploadedAudio();
                    }}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300
                               font-medium transition-colors duration-200"
                  >
                    Usuń
                  </button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm"
              className="hidden"
            />
          </div>
        ) : uploadMethod === 'record' ? (
          // Live Recording Section
          <LiveRecording
            sessionId={sessionId}
            salesStage={salesStage}
            onRecordingReady={onRecordingReady}
            onRecordingComplete={(result) => {
              if (result.type === 'async_job') {
                onAudioUrlChange(''); // Clear audio URL for live recording jobs
                onAsyncJobStart?.(result.jobId);
              } else {
                onAudioUrlChange(result.audioUrl);
              }
            }}
          />
        ) : null}

        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 
                          rounded-2xl p-4 animate-fade-in">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 dark:text-red-400 font-medium">
                {error}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};